import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Button,
  IconButton,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { MethodEditorProps } from '../types';
import { FORM_FIELD_EXAMPLES } from '../constants';

export const SetFormFieldsEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  // Ensure payload always has the required structure and filter out empty keys
  const cleanFields = payload?.fields ? Object.fromEntries(Object.entries(payload.fields)) : {};

  const normalizedPayload = {
    form: '',
    streamingDelay: 50,
    ...payload,
    fields: cleanFields,
  };

  const addField = () => {
    onChange({
      ...normalizedPayload,
      fields: {
        ...normalizedPayload.fields,
        '': '', // Add empty field that user can edit
      },
    });
  };

  const updateFieldKey = (oldKey: string, newKey: string) => {
    const newFields = { ...normalizedPayload.fields };
    const value = newFields[oldKey];

    // Remove old key
    delete newFields[oldKey];

    // Add with new key if key is not empty
    if (newKey.trim()) {
      newFields[newKey.trim()] = value;
    }

    onChange({ ...normalizedPayload, fields: newFields });
  };

  const updateFieldValue = (key: string, newValue: string) => {
    onChange({
      ...normalizedPayload,
      fields: {
        ...normalizedPayload.fields,
        [key]: newValue,
      },
    });
  };

  const removeField = (key: string) => {
    const { [key]: removed, ...rest } = normalizedPayload.fields;
    onChange({ ...normalizedPayload, fields: rest });
  };

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel>Form</InputLabel>
        <Select
          value={normalizedPayload.form}
          onChange={(e) => onChange({ ...normalizedPayload, form: e.target.value })}
        >
          {FORM_FIELD_EXAMPLES.map((form) => (
            <MenuItem key={form} value={form}>
              {form}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        type="number"
        value={normalizedPayload.streamingDelay}
        onChange={(e) =>
          onChange({ ...normalizedPayload, streamingDelay: parseInt(e.target.value, 10) })
        }
        label="Streaming Delay (ms)"
        helperText="Milliseconds between chunks for text streaming animation"
        margin="normal"
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">Form Fields</Typography>
        <Button onClick={addField} startIcon={<AddIcon />} variant="outlined" size="small">
          Add Field
        </Button>
      </Box>

      {/* Existing fields */}
      {Object.entries(normalizedPayload.fields).map(([key, value], index) => (
        <Paper
          key={key || `empty-${index}`}
          sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <TextField
            size="small"
            value={key}
            onChange={(e) => updateFieldKey(key, e.target.value)}
            label="Field Name"
            placeholder="Enter field name"
          />
          <TextField
            size="small"
            fullWidth
            value={typeof value === 'string' ? value : JSON.stringify(value)}
            onChange={(e) => updateFieldValue(key, e.target.value)}
            label="Field Value"
            placeholder="{{variableName}} or $context:path or literal value"
          />
          <IconButton onClick={() => removeField(key)} color="error">
            <DeleteIcon />
          </IconButton>
        </Paper>
      ))}

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Template Variables:</strong> Use {'{{'}
          {'{variableName}'}
          {'}}'} for context values
          <br />
          <strong>Context References:</strong> Use $context:path for direct lookups
          <br />
          <strong>Streaming:</strong> Text fields will animate with the specified delay
        </Typography>
      </Alert>
    </Box>
  );
};
